import { isPendingAffiliationRequest } from "../../affiliation/helpers/affiliationRequest";
import { searchProvidedAffiliations } from "../../affiliation/services/affiliationSearchService";
import { findReceivedProvider } from "../../affiliation/services/teleconsultoriaAffiliationService";
import type { TeleconsultoriaProfile } from "../types";

export const PROFILE_VERIFICATION_ERROR =
  "Não foi possível verificar a afiliação de teleconsultoria da sua clínica.";

export function emptyProfile(organizationToken = ""): TeleconsultoriaProfile {
  return { kind: "none", organizationToken, providerToken: "", providerName: "" };
}

/**
 * A organização já tem afiliadas? Ativas ou inativas: desativar todas não a tira de prestadora.
 * Pedido de afiliada ainda não aprovado NÃO conta (receber um pedido não torna prestadora).
 */
async function hasProvidedAffiliations(organizationToken: string): Promise<boolean> {
  const { affiliations } = await searchProvidedAffiliations(organizationToken);
  return affiliations.some((affiliation) => !isPendingAffiliationRequest(affiliation));
}

/**
 * Regra do perfil:
 * 1. Afiliação HIE/HIO ativa como receptora → afiliada (mesmo com contrato:
 *    quem recebe não presta).
 * 2. Com contrato e ao menos 1 afiliação como prestadora → prestadora.
 * 3. Com contrato e sem relação → "unassigned" (a tela pede: prestar ou receber).
 * 4. Senão → nenhum.
 * Falha na busca: com contrato continua prestadora (só dados da própria
 * organização, como antes); sem contrato fica "nenhum". O erro sobe para a UI mostrar.
 */
export async function resolveTeleconsultoriaProfile(params: {
  organizationToken: string;
  hasContract: boolean;
}): Promise<{ profile: TeleconsultoriaProfile; errorMessage?: string }> {
  const { organizationToken, hasContract } = params;
  if (!organizationToken) return { profile: emptyProfile() };

  const providerProfile: TeleconsultoriaProfile = {
    kind: "provider",
    organizationToken,
    providerToken: organizationToken,
    providerName: "",
  };

  try {
    // Sem contrato não há como prestar: nem busca as afiliadas.
    const [received, isProvider] = await Promise.all([
      findReceivedProvider(organizationToken),
      hasContract ? hasProvidedAffiliations(organizationToken) : Promise.resolve(false),
    ]);
    if (received) {
      return {
        profile: {
          kind: "affiliate",
          organizationToken,
          providerToken: received.providerToken,
          providerName: received.providerName,
        },
      };
    }
    if (!hasContract) return { profile: emptyProfile(organizationToken) };
    return {
      profile: isProvider
        ? providerProfile
        : { ...emptyProfile(organizationToken), kind: "unassigned" },
    };
  } catch (error) {
    console.error("resolveTeleconsultoriaProfile", error);
    return {
      profile: hasContract ? providerProfile : emptyProfile(organizationToken),
      errorMessage: PROFILE_VERIFICATION_ERROR,
    };
  }
}
